import { useMediaQuery } from "@mui/material";
import { FC, useMemo } from "react";
import { EditIcon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import Text from "../../../components/Text";
import { MASKED_PERFORMANCE_VALUE } from "../../../utils/tradesPerfKpisCookie";
import styles from "./styles.module.scss";

const CURRENCY_FORMATTER = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
});
const formatCurrency = (value: number) =>
    `${CURRENCY_FORMATTER.format(value)} $`;

type Props = {
    loading: boolean;
    status: {
        isFunded: boolean;
        fundedAccountsCount: number;
        fundedTotalBalance: string;
        asOfDate: string;
    } | null;
    /** Payouts (total et moyen) à afficher dans le Hero */
    payoutSection?: { total: number; average: number };
    payoutsLoading?: boolean;
    hidePerformance: boolean;
    /** Afficher le bouton Mettre à jour. false = vue admin sur un autre user (lecture seule). */
    canEdit?: boolean;
    onEdit: () => void;
};

const FundingStatusHero: FC<Props> = ({
    loading,
    status,
    payoutSection,
    payoutsLoading = false,
    hidePerformance,
    canEdit = true,
    onEdit,
}) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const displayValue = (
        isLoading: boolean,
        value: string | number | undefined
    ) =>
        isLoading || value === undefined
            ? "-"
            : hidePerformance
              ? MASKED_PERFORMANCE_VALUE
              : value;
    const { isOutdated, freshnessLabel, lastUpdateLabel } = useMemo(() => {
        if (!status) {
            return {
                isOutdated: true,
                freshnessLabel: "Aucune mise à jour enregistrée",
                lastUpdateLabel: "",
            };
        }

        const last = new Date(status.asOfDate);
        const now = new Date();
        const diffMs = now.getTime() - last.getTime();
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const outdated = days > 30;

        const daysLabel =
            days === 0
                ? "aujourd'hui"
                : `il y a ${days} jour${days > 1 ? "s" : ""}`;

        return {
            isOutdated: outdated,
            freshnessLabel: outdated ? "Mise à jour recommandée" : "Statut à jour",
            lastUpdateLabel: `Statut mis à jour ${daysLabel}`,
        };
    }, [status]);

    return (
        <section className={styles.hero}>
            <div className={styles.headerRow}>
                <Text variant="title" className={styles.title}>
                    {status?.isFunded ? (
                        isMobile ? (
                            <span className={styles.titleMobileRow}>
                                <span className={styles.titleTrophy}>🏆</span>
                                <span className={styles.titleMobileText}>
                                    <span>Trader</span>
                                    <span>financé</span>
                                </span>
                            </span>
                        ) : (
                            <>
                                <span className={styles.titleTrophy}>🏆</span>{" "}
                                Trader financé
                            </>
                        )
                    ) : (
                        isMobile ? (
                            <span className={styles.titleMobileRow}>
                                <span className={styles.titleIcon}>🎓</span>
                                <span className={styles.titleMobileText}>
                                    <span>Trader non</span>
                                    <span>financé</span>
                                </span>
                            </span>
                        ) : (
                            <>
                                <span className={styles.titleIcon}>🎓</span>{" "}
                                Trader non financé
                            </>
                        )
                    )}
                </Text>
                {canEdit && (
                    <div className={styles.actions}>
                        <Button
                            size="small"
                            variant="primary"
                            className={styles.actionButton}
                            Icon={EditIcon}
                            iconSize={16}
                            label={isMobile ? "modifier" : "Modifier mon financement"}
                            onClick={onEdit}
                        />
                    </div>
                )}
            </div>

            <div className={styles.kpisRow}>
                <div className={styles.kpiCard}>
                    <span className={styles.kpiLabel}>Nb de comptes financés</span>
                    <span className={styles.kpiValue}>
                        {displayValue(
                            loading,
                            status?.fundedAccountsCount
                        )}
                    </span>
                </div>
                <div className={styles.kpiCard}>
                    <span className={styles.kpiLabel}>
                        Capital total déclaré (compte +PnL)
                    </span>
                    <span className={styles.kpiValue}>
                        {displayValue(
                            loading,
                            status
                                ? formatCurrency(
                                      Number(
                                          status.fundedTotalBalance || "0"
                                      )
                                  )
                                : undefined
                        )}
                    </span>
                </div>
                <div className={styles.kpiCardPayout}>
                    <span className={styles.kpiLabel}>Total payouts déclarés</span>
                    <span className={styles.kpiValue}>
                        {displayValue(
                            payoutsLoading,
                            payoutSection
                                ? formatCurrency(payoutSection.total)
                                : undefined
                        )}
                    </span>
                    <span className={styles.kpiLabelSmall}>payout moyen</span>
                    <span className={styles.kpiValueSmall}>
                        {displayValue(
                            payoutsLoading,
                            payoutSection
                                ? formatCurrency(payoutSection.average)
                                : undefined
                        )}
                    </span>
                </div>
            </div>

            <div className={styles.footerRow}>
                <div className={styles.freshnessBlock}>
                    <div
                        className={`${styles.freshnessPill} ${
                            isOutdated
                                ? styles.freshnessPillOutdated
                                : styles.freshnessPillOk
                        }`}
                    >
                        <span className={styles.lastUpdateLabel}>
                            {!loading && status && lastUpdateLabel
                                ? lastUpdateLabel
                                : freshnessLabel}
                        </span>
                        {isOutdated && status && !loading && (
                            <span className={styles.freshnessRecommendation}>
                                Mise à jour recommandée
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FundingStatusHero;
